// © Microsoft Corporation. All rights reserved.

import { canUseDOM, getLocationSearch } from "@msnews/isomorphic-util/";

/**
 * Defines telemetryReadyCallback() which queues a function to be called after the page is revealed.
 * This is specific for telemetry package and should not be used for preload dependent features.
 * Telemetry can't have a reference to preload since preload references experiences-redux which
 * then references telemetry which causes a problem.
 * This helper is specific to queuing telemetry calls when needed and invoking the queued calls once
 * the preload page is revealed.
 */

let queuedCalls: Function[] = [];
const locationSearch = getLocationSearch();
let isPreload = locationSearch.match(/backgroundpreload/) ? true : false;
let pageRevealed = false;

let trackValuesUpdated = true;

// Check and add an event listener if page has been pre-loaded but not yet revealed.
if (canUseDOM() && isPreload && document.visibilityState !== "visible") {
    document.addEventListener("visibilitychange", onVisibilityChange);
} else {
    pageRevealed = true;
}

/** Event handler for page visibility change */
function onVisibilityChange(): void {
    if (canUseDOM()) {
        document.removeEventListener("visibilitychange", onVisibilityChange);
    }
    pageRevealed = true;
    checkAndFireQueuedCalls();
}

/** Event handler to run once track values have been updated by respective entities */
function onTrackValuesUpdated(): void {
    if (canUseDOM()) {
        document.removeEventListener("trackvaluesupdated", onTrackValuesUpdated);
    }
    trackValuesUpdated = true;
    checkAndFireQueuedCalls();
}

/** Helper method to check if all conditions have been satisfied and then fire queued up telemetry beacons */
function checkAndFireQueuedCalls(): void {
    if (trackValuesUpdated && pageRevealed) {
        while (queuedCalls.length) {
            let item = queuedCalls.shift();
            if (typeof item === "function") {
                item();
            }
        }
    }
}

/** If config dictates to wait for page settings to update track values, set trackValuesUpdated to false and add an event listener for trackvaluesupdated. */
export function waitForPageSettingsTrackUpdate(waitForPageSettingsToUpdateTrack: boolean): void {
    if (!waitForPageSettingsToUpdateTrack) {
        return;
    }

    trackValuesUpdated = false;

    if (canUseDOM()) {
        // Add an event listener to listen for a signal indicating track values have been updated.
        document.addEventListener("trackvaluesupdated", onTrackValuesUpdated);
    }
}

/**
 * If the page is revealed then just call the callback, if not then queue it up
 *
 * @param {function(object): void} callback - The callback function.
 * @returns {void}
 */
export function telemetryReadyCallback(callback: Function): void {

    if (pageRevealed && trackValuesUpdated) {
        callback();
    } else {
        queuedCalls.push(callback);
    }
}
